import { daysToMs, type FetchCurrenciesParamsTypes, type WalletCurrency } from '@capsicum/common';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useMe } from '../../../auth/hooks/useMe';
import { type MainCurrencies, Regulation, ShortCode } from '../../models';
import { fetchCurrencies } from '../api/currencies';

/**
 *
 * @returns default and alternative wallet currencies eg. CHZ and EUR
 */
export const useMainCurrenciesQuery = (types?: Array<FetchCurrenciesParamsTypes>) => {
	const { me, meError } = useMe();

	if (meError) throw meError;

	const defaultCurrencyShortCode =
		me.regulation === Regulation.International ? ShortCode.CHZ : me.preferredFiatCurrencyCode;

	const altCurrencyShortCode = me.regulation === Regulation.International ? me.preferredFiatCurrencyCode : undefined;

	const shortCodes = [defaultCurrencyShortCode, altCurrencyShortCode].filter(Boolean) as Array<string>;

	return useQuery({
		enabled: Boolean(shortCodes.length),
		queryFn: () =>
			fetchCurrencies({
				'shortCodes[]': shortCodes,
				...(types?.length ? { 'types[]': types } : {})
			}),
		queryKey: ['mainCurrencies', { shortCodes, types, typesLength: types?.length }],
		select: useCallback(
			(currencies: Array<WalletCurrency> | undefined): MainCurrencies | undefined =>
				currencies?.reduce(
					(mainCurrencies, currency) => ({
						...mainCurrencies,

						...(currency.shortCode === defaultCurrencyShortCode && { defaultCurrency: currency }),

						...(currency.shortCode === altCurrencyShortCode && { altCurrency: currency }),

						convertAmountToCurrencies: [...mainCurrencies.convertAmountToCurrencies, currency.id!]
					}),
					{
						altCurrency: {},
						convertAmountToCurrencies: [],
						defaultCurrency: {}
					} as MainCurrencies
				),
			[defaultCurrencyShortCode, altCurrencyShortCode]
		),
		staleTime: daysToMs(1)
	});
};
